import { useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import parse from "html-react-parser";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useApolloClient } from "@apollo/client";
import { Link } from "react-router-dom";
import AppContext from "../services/context.js";
import { GET_ITEM } from "../services/api.js";
import { buildPrescriptionText } from "../utils/prescriptionText.js";
import { useToast } from "../hooks/useToast.js";
import { useError } from "../hooks/useError.js";

marked.setOptions({ gfm: true, breaks: true });

const AiSearchResult = () => {
  const { aiResponse, aiLoading, isAiMode, themeKey, searchTerm } = useContext(AppContext);
  const client = useApolloClient();
  const toast = useToast();
  const { showError } = useError();
  const [specieDetails, setSpecieDetails] = useState({});
  const [specieLoading, setSpecieLoading] = useState(false);

  const processedItems = useMemo(() => {
    if (!aiResponse) {
      return [];
    }

    const formatItem = (item, index) => {
      if (!item || typeof item.text !== "string") {
        return null;
      }

      const trimmedText = item.text.trim();

      if (!trimmedText) {
        return null;
      }

      const specieIdValue =
        typeof item.specieId === "number"
          ? item.specieId
          : typeof item.specie_id === "number"
            ? item.specie_id
            : null;

      const parsedHtml = marked.parse(trimmedText);
      const sanitizedHtml = DOMPurify.sanitize(parsedHtml, { USE_PROFILES: { html: true } });

      return {
        key: specieIdValue ? `specie-${specieIdValue}` : `item-${index}`,
        specieId: specieIdValue,
        hasInternalReference: Boolean(
          specieIdValue &&
          (
            trimmedText.includes(`/especie/${specieIdValue}`) ||
            /Página da espécie no Greenbook/i.test(trimmedText)
          )
        ),
        html: sanitizedHtml,
      };
    };

    if (Array.isArray(aiResponse.items)) {
      return aiResponse.items.map(formatItem).filter(Boolean);
    }

    if (Array.isArray(aiResponse)) {
      return aiResponse.map(formatItem).filter(Boolean);
    }

    if (typeof aiResponse?.answer === "string" && aiResponse.answer.trim()) {
      return [
        formatItem(
          {
            text: aiResponse.answer,
            specieId: typeof aiResponse.specieId === "number" ? aiResponse.specieId : null,
          },
          0
        ),
      ].filter(Boolean);
    }

    return [];
  }, [aiResponse]);

  const specieIds = useMemo(
    () => [...new Set(processedItems.map((item) => item.specieId).filter(Boolean))],
    [processedItems]
  );

  const relatedSpecies = useMemo(
    () => (Array.isArray(aiResponse?.relatedSpecies) ? aiResponse.relatedSpecies : []),
    [aiResponse]
  );

  useEffect(() => {
    let isCancelled = false;

    const loadSpecieDetails = async () => {
      if (specieIds.length === 0) {
        setSpecieDetails({});
        setSpecieLoading(false);
        return;
      }

      setSpecieLoading(true);

      try {
        let detailError = null;
        const entries = await Promise.all(
          specieIds.map(async (specieId) => {
            try {
              const { data } = await client.query({
                query: GET_ITEM,
                variables: { q: `especie:${specieId}` },
              });

              return [specieId, data?.getItem ?? null];
            } catch (error) {
              detailError = error;
              return [specieId, null];
            }
          })
        );

        if (!isCancelled) {
          setSpecieDetails(Object.fromEntries(entries));
          if (detailError) {
            showError(detailError, { fallback: "Não foi possível carregar detalhes de uma ou mais espécies." });
          }
        }
      } finally {
        if (!isCancelled) {
          setSpecieLoading(false);
        }
      }
    };

    loadSpecieDetails();

    return () => {
      isCancelled = true;
    };
  }, [client, showError, specieIds]);

  const isDarkTheme = themeKey === "dark";
  const hasAnswer = processedItems.length > 0;
  const errorMessage = typeof aiResponse?.error === "string" ? aiResponse.error : null;

  const suggestionButtonClasses = clsx(
    "inline px-4 py-2 rounded-lg mr-1 outline-none cursor-pointer",
    isDarkTheme
      ? [
          "bg-greenbook-dark-bg text-greenbook-dark-text border border-greenbook-dark-primary",
          "hover:bg-greenbook-dark-primary hover:text-white",
        ]
      : [
          "bg-greenbook-light-bg text-greenbook-light-text border border-gray-300",
          "hover:bg-green-700 hover:text-white",
        ]
  );

  const COPY_SUCCESS_MESSAGE = "A prescrição foi copiada para área de transferência";

  const handleCopyToClipboard = async (text) => {
    if (!text) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(COPY_SUCCESS_MESSAGE);
      } else {
        throw new Error("Clipboard API indisponível");
      }
    } catch (error) {
      toast.error("Não foi possível copiar a prescrição.");
    }
  };

  const handleSuggestionSelection = (suggestion, fallbackLabel) => {
    const prescriptionText = buildPrescriptionText(suggestion);

    handleCopyToClipboard(prescriptionText || fallbackLabel);
  };

  if (!isAiMode) {
    return null;
  }

  return (
    <>
      <section className="px-4 py-8 max-w-[900px] mx-auto w-full">
      <div
        className={clsx(
          "rounded-3xl border backdrop-blur px-6 py-8 shadow-[0_20px_60px_rgba(168,85,247,0.25)] transition-colors",
          isDarkTheme
            ? "bg-purple-900/20 border-purple-500/40 text-purple-100"
            : "bg-white border-purple-200 text-purple-900"
      )}
      >
        <header className="mb-6">
          <p className="uppercase tracking-[0.3em] text-xs font-semibold opacity-70">
            Resposta Assistida por IA
          </p>
          <h2
            className={clsx(
              "font-montserrat text-3xl font-bold",
              isDarkTheme ? "text-purple-200" : "text-purple-700"
            )}
          >
            {searchTerm || "Explorar fitoterapia com IA"}
          </h2>
        </header>

        {aiLoading && (
          <p className="text-sm animate-pulse">
            Elaborando uma resposta personalizada com base no seu interesse...
          </p>
        )}

        {!aiLoading && hasAnswer && (
          <div className="space-y-8 text-base leading-relaxed">
            {processedItems.map((item) => {
              const specieData = item.specieId ? specieDetails[item.specieId] : null;
              const isSpecieLoading = Boolean(item.specieId) && specieLoading && !specieData;

              return (
                <article key={item.key} className="space-y-6">
                  <div className="space-y-4 text-sm leading-6 ai-response-content">
                    {parse(item.html)}
                  </div>

                  {item.specieId && !item.hasInternalReference && (
                    <p className="text-sm">
                      <strong>Referência interna:</strong>{" "}
                      <Link
                        to={`/especie/${item.specieId}`}
                        className={clsx(
                          "font-semibold underline underline-offset-2",
                          isDarkTheme ? "text-purple-200" : "text-purple-700"
                        )}
                      >
                        {specieData?.title
                          ? `Página da espécie ${specieData.title}`
                          : "Página da espécie"}
                      </Link>
                    </p>
                  )}

                  {isSpecieLoading && (
                    <p className="text-sm opacity-70">Carregando sugestões personalizadas...</p>
                  )}

                  {Boolean(specieData?.prescriptionSuggestions?.length) && (
                    <div className="space-y-3">
                      <header>
                        <h3 className="text-lg font-semibold">
                          Sugestões de Prescrição
                          {specieData?.title ? ` · ${specieData.title}` : ""}
                        </h3>
                        <p className="text-sm italic">
                          Clique em uma das prescrições abaixo para copiar:
                        </p>
                      </header>
                      <ul className="space-y-2">
                        {specieData.prescriptionSuggestions.map((suggestion, index) => {
                          const specieName = suggestion.specie?.name ?? "";
                          const partName = suggestion.part?.name ?? "";
                          const dosage = suggestion.dosage ?? "";
                          const buttonLabel = [
                            specieName,
                            partName ? `(${partName})` : "",
                            dosage,
                          ]
                            .filter(Boolean)
                            .join(" ");

                          return (
                            <li key={`suggestion-${index}`} className="list-none">
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSuggestionSelection(suggestion, buttonLabel)}
                                  className={suggestionButtonClasses}
                                >
                                  {buttonLabel}
                                </button>
                                {(suggestion.description || suggestion.quantity) && (
                                  <p className="text-sm">
                                    {suggestion.quantity && <span>{suggestion.quantity}</span>}
                                    {suggestion.description && suggestion.quantity && <span> - </span>}
                                    {suggestion.description && <span>{suggestion.description}</span>}
                                  </p>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </article>
              );
            })}

            {relatedSpecies.length > 0 && (
              <aside
                className={clsx(
                  "rounded-2xl border p-4",
                  isDarkTheme
                    ? "border-purple-500/40 bg-purple-950/30"
                    : "border-purple-200 bg-purple-50"
                )}
              >
                <h3 className="font-semibold">Outras plantas relacionadas</h3>
                <ul className="mt-3 space-y-3 text-sm">
                  {relatedSpecies.map((specie) => (
                    <li key={`related-${specie.specieId}`}>
                      <Link
                        to={`/especie/${specie.specieId}`}
                        className={clsx(
                          "font-semibold underline underline-offset-2",
                          isDarkTheme ? "text-purple-200" : "text-purple-700"
                        )}
                      >
                        {specie.name}
                      </Link>
                      <span>{` — ${specie.reason}`}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>
        )}

        {!aiLoading && !hasAnswer && !errorMessage && (
          <p className="text-sm opacity-70">
            Descreva sua necessidade em fitoterapia e toque em Perguntar para receber um resumo inteligente.
          </p>
        )}

        {!aiLoading && !hasAnswer && errorMessage && (
          <p className="text-sm text-red-500">{errorMessage}</p>
        )}
      </div>
      </section>
    </>
  );
};

export default AiSearchResult;
